/**
 * Period, as-of and budget selection, kept in the URL so it survives navigation and reloads and a
 * link shows exactly the same view (fpa-backend ADR-0006):
 *
 *   ?y=2026&m=7,8,9&asof=2026-08-26&budget=<Sage budget id>
 *
 * Absent `y` = the current quarter, as of today ("Live", server side); `y` without `m` = the full
 * year; absent `asof` = the latest date the period allows. Older links keep working: `?q=2026-Q3`
 * maps to that quarter and the legacy `?asOf=YYYY-MM-DD` alone to the quarter containing it, as of
 * it. Any change rewrites them to the new form.
 */
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { PeriodSelection } from '@/api/fpa'
import { isIsoDate, normalizeMonths, quarterMonths, quarterOfMonth, sameMonths } from '@/lib/period'

const QUARTER = /^(\d{4})-Q([1-4])$/
const YEAR = /^\d{4}$/
const BUDGET_MAX = 100

/** The search params every in-app link carries. */
export const CARRIED_PARAMS = ['y', 'm', 'asof', 'q', 'asOf', 'budget'] as const
const PERIOD_KEYS = ['y', 'm', 'asof', 'q', 'asOf'] as const

function parseMonthList(value: string | null): number[] {
  if (!value) return []
  return normalizeMonths(value.split(',').map((part) => (/^\d{1,2}$/.test(part.trim()) ? Number(part) : NaN)))
}

/** The period part of the URL (budget excluded); `{}` = the default live view. */
export function parsePeriod(params: URLSearchParams): Omit<PeriodSelection, 'budgetId'> {
  const y = params.get('y') ?? ''
  const asof = params.get('asof') ?? ''
  if (YEAR.test(y)) {
    const months = parseMonthList(params.get('m'))
    return {
      year: Number(y),
      months: months.length > 0 ? months : undefined,
      asOf: isIsoDate(asof) ? asof : undefined,
    }
  }
  const q = QUARTER.exec(params.get('q') ?? '')
  if (q) {
    return {
      year: Number(q[1]),
      months: quarterMonths(Number(q[2])),
      asOf: isIsoDate(asof) ? asof : undefined,
    }
  }
  const legacy = params.get('asOf') ?? ''
  if (isIsoDate(legacy)) {
    return {
      year: Number(legacy.slice(0, 4)),
      months: quarterMonths(quarterOfMonth(Number(legacy.slice(5, 7)))),
      asOf: legacy,
    }
  }
  return {}
}

export function parsePeriodParams(params: URLSearchParams): PeriodSelection {
  const budget = (params.get('budget') ?? '').trim()
  return {
    ...parsePeriod(params),
    budgetId: budget && budget.length <= BUDGET_MAX ? budget : undefined,
  }
}

/** Writes a period into `params` in the canonical form (legacy keys dropped). */
export function writePeriod(params: URLSearchParams, sel: Omit<PeriodSelection, 'budgetId'>): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of PERIOD_KEYS) next.delete(key)
  if (sel.year !== undefined) {
    next.set('y', String(sel.year))
    if (sel.months && sel.months.length > 0 && sel.months.length < 12) next.set('m', sel.months.join(','))
    if (sel.asOf) next.set('asof', sel.asOf)
  }
  return next
}

/** `?y=…&m=…&asof=…&budget=…` for links (empty string when nothing is selected). */
export function carriedSearch(params: URLSearchParams): string {
  const out = new URLSearchParams()
  for (const key of CARRIED_PARAMS) {
    const value = params.get(key)
    if (value) out.set(key, value)
  }
  const qs = out.toString()
  return qs ? `?${qs}` : ''
}

export function usePeriodSelection(): PeriodSelection {
  const [params] = useSearchParams()
  return useMemo(() => parsePeriodParams(params), [params])
}

/** The period part of the current URL, to append to in-app links. */
export function useCarriedSearch(): string {
  const [params] = useSearchParams()
  return carriedSearch(params)
}

/** Today and the current quarter's months: what "Live" means. */
export interface LiveContext {
  /** YYYY-MM-DD (America/New_York). */
  today: string
}

export function isDefaultPeriod(year: number, months: readonly number[], live: LiveContext): boolean {
  const ty = Number(live.today.slice(0, 4))
  const tq = quarterOfMonth(Number(live.today.slice(5, 7)))
  return year === ty && sameMonths(months, quarterMonths(tq))
}

export interface PeriodActions {
  /**
   * Pick a year and months; the as-of returns to the latest and the budget to FPA's resolved one.
   * The current quarter drops the params (the live view).
   */
  setPeriod: (year: number, months: readonly number[], live: LiveContext) => void
  /** Pick an as-of inside the shown period; `null` = the latest it allows. */
  setAsOf: (asOf: string | null, shown: { year: number; months: readonly number[] }) => void
  /** The default view: the current quarter as of today. */
  jumpToLive: () => void
  /** Pick a budget; `null` returns to FPA's resolved budget. */
  setBudget: (budgetId: string | null) => void
}

export function usePeriodActions(): PeriodActions {
  const [, setParams] = useSearchParams()
  const setPeriod = useCallback(
    (year: number, monthsIn: readonly number[], live: LiveContext) =>
      setParams((prev) => {
        const months = normalizeMonths(monthsIn)
        const base = new URLSearchParams(prev)
        base.delete('budget')
        if (months.length === 0 || isDefaultPeriod(year, months, live)) return writePeriod(base, {})
        return writePeriod(base, { year, months })
      }),
    [setParams],
  )
  const setAsOf = useCallback(
    (asOf: string | null, shown: { year: number; months: readonly number[] }) =>
      setParams((prev) => writePeriod(prev, { year: shown.year, months: [...shown.months], asOf: asOf ?? undefined })),
    [setParams],
  )
  const jumpToLive = useCallback(
    () =>
      setParams((prev) => {
        const next = writePeriod(prev, {})
        next.delete('budget')
        return next
      }),
    [setParams],
  )
  const setBudget = useCallback(
    (budgetId: string | null) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        if (budgetId) next.set('budget', budgetId)
        else next.delete('budget')
        return next
      }),
    [setParams],
  )
  return useMemo(() => ({ setPeriod, setAsOf, jumpToLive, setBudget }), [setPeriod, setAsOf, jumpToLive, setBudget])
}
